/**
 * Position Comment Form Component
 *
 * Modal form for adding/editing position comments.
 * Supports two modes:
 * - Add new comment (creates new record)
 * - Edit existing comment (in-place update)
 *
 * Includes character counter and optional revisit date picker.
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Alert,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import {
  useAddPositionComment,
  useUpdatePositionComment,
} from '../../services/api/queries/usePositionComments';
import type { PositionComment } from '../../services/api/positionCommentApi';
import { DatePicker } from '../ui/DatePicker';

interface PositionCommentFormProps {
  positionId: number;
  onSuccess: () => void;
  // For editing existing comment
  existingComment?: PositionComment;
}

const MAX_CHARS = 2000;

export function PositionCommentForm({
  positionId,
  onSuccess,
  existingComment,
}: PositionCommentFormProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [commentText, setCommentText] = useState(existingComment?.comment_text || '');
  const [nextRevisitDate, setNextRevisitDate] = useState(existingComment?.next_revisit_date || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addMutation = useAddPositionComment();
  const updateMutation = useUpdatePositionComment();

  const isEditMode = !!existingComment;
  const charCount = commentText.length;
  const isValid = commentText.trim().length > 0 && charCount <= MAX_CHARS;

  const handleSubmit = async () => {
    if (!isValid) return;

    // Validate date format if provided
    if (nextRevisitDate && nextRevisitDate.trim() !== '') {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(nextRevisitDate.trim())) {
        Alert.alert(
          t('common.error', { defaultValue: 'Error' }),
          t('portfolio.positionComment.invalidDateFormat', {
            defaultValue: 'Please use YYYY-MM-DD format for the date',
          })
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const submitDate = nextRevisitDate?.trim() || null;

      if (isEditMode) {
        // In-place edit
        await updateMutation.mutateAsync({
          commentId: existingComment!.id,
          commentText: commentText.trim(),
          nextRevisitDate: submitDate,
        });
      } else {
        // Create new comment
        await addMutation.mutateAsync({
          positionId,
          commentText: commentText.trim(),
          nextRevisitDate: submitDate,
        });
      }
      onSuccess();
    } catch (error: any) {
      Alert.alert(
        t('common.error', { defaultValue: 'Error' }),
        error.message ||
          t('portfolio.positionComment.saveFailed', { defaultValue: 'Failed to save comment' })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const charCountColor =
    charCount > MAX_CHARS * 0.9 ? theme.colors.error : theme.colors.textSecondary;

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.keyboardContainer}
    >
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Comment Text Area */}
        <View style={styles.formSection}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.positionComment.commentText', { defaultValue: 'Comment' })}
            <Text style={{ color: theme.colors.error }}> *</Text>
          </Text>

          <View
            style={[
              styles.textAreaContainer,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: charCount > MAX_CHARS ? theme.colors.error : theme.colors.border,
                borderWidth: charCount > MAX_CHARS ? 2 : theme.id === 'bauhaus' ? 2 : 1,
              },
            ]}
          >
            <TextInput
              style={[styles.textInput, { color: theme.colors.text }]}
              placeholder={t('portfolio.positionComment.placeholder', {
                defaultValue: 'Add your quick notes about this position...',
              })}
              placeholderTextColor={theme.colors.textSecondary}
              value={commentText}
              onChangeText={setCommentText}
              multiline
              numberOfLines={6}
              maxLength={MAX_CHARS + 1} // Allow one extra to show over limit
              textAlignVertical="top"
              autoFocus={!isEditMode}
            />

            {/* Character Counter */}
            <View style={styles.charCounterContainer}>
              <Text style={[styles.charCounter, { color: charCountColor }]}>
                {charCount} / {MAX_CHARS}
              </Text>
            </View>
          </View>

          {charCount > MAX_CHARS && (
            <Text style={[styles.errorText, { color: theme.colors.error }]}>
              {t('portfolio.positionComment.tooLong', {
                defaultValue: 'Comment exceeds maximum length',
              })}
            </Text>
          )}
        </View>

        {/* Revisit Date */}
        <View style={styles.formSection}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.positionComment.nextRevisitDate', {
              defaultValue: 'Next Revisit Date (Optional)',
            })}
          </Text>

          <DatePicker
            value={nextRevisitDate}
            onChange={(date) => setNextRevisitDate(date || '')}
            placeholder="YYYY-MM-DD"
          />

          <Text style={[styles.hintText, { color: theme.colors.textSecondary }]}>
            {t('portfolio.positionComment.dateHint', {
              defaultValue: 'Set a reminder date to revisit this position',
            })}
          </Text>
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[
              styles.submitButton,
              {
                backgroundColor:
                  isValid && !isSubmitting ? theme.colors.primary : theme.colors.border,
              },
              isEditMode && styles.editButton,
            ]}
            onPress={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? (
              <Text style={[styles.submitButtonText, { color: theme.colors.background }]}>
                {t('common.saving', { defaultValue: 'Saving...' })}
              </Text>
            ) : (
              <>
                <Ionicons
                  name={isEditMode ? 'checkmark-circle' : 'add-circle'}
                  size={20}
                  color={isValid ? theme.colors.background : theme.colors.textSecondary}
                />
                <Text
                  style={[
                    styles.submitButtonText,
                    {
                      color: isValid ? theme.colors.background : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {isEditMode
                    ? t('portfolio.positionComment.save', { defaultValue: 'Save Changes' })
                    : t('portfolio.positionComment.addComment', { defaultValue: 'Add Comment' })}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    keyboardContainer: {
      flex: 1,
    },
    scrollContainer: {
      flex: 1,
    },
    scrollContent: {
      padding: 16,
      paddingBottom: 32,
    },
    formSection: {
      marginBottom: 20,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    textAreaContainer: {
      borderRadius: theme.id === 'bauhaus' ? 0 : theme.radii.md,
      padding: 12,
      minHeight: 120,
    },
    textInput: {
      flex: 1,
      fontSize: theme.typography.fontSizeMd,
      lineHeight: 20,
      minHeight: 100,
    },
    charCounterContainer: {
      alignItems: 'flex-end',
      marginTop: 8,
    },
    charCounter: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightMedium,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
    },
    hintText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 6,
      fontStyle: 'italic',
    },
    buttonContainer: {
      gap: 12,
      marginTop: 8,
    },
    submitButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: theme.id === 'bauhaus' ? 0 : theme.radii.md,
      gap: 8,
    },
    editButton: {
      // For edit mode, we can add a subtle visual distinction
    },
    submitButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 1 : 0,
    },
  });
}
